import { createInstance, dateReviver } from '@/api';
import {
    DeliveryProcessDefinitionClient,
    OrderDirection,
    DeliveryProcessDefinitionSearchResultDto,
    OrganisationSearchResultDto
} from '@/api/backoffice/clients';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';

export interface DeliveryProcessDefinitionFilter {
    id?: number | null;
    label?: string;
    description?: string;
    order?: string;
    orderDirection?: OrderDirection;
    pageNumber?: number;
    pageSize?: number;
    organisation?: OrganisationSearchResultDto;
    statusType?: string;
}

export interface DeliveryProcessDefinitionStoreState {
    filter: DeliveryProcessDefinitionFilter;
    loading: boolean;
    items?: DeliveryProcessDefinitionSearchResultDto[];
    totalCount?: number;
}

// Must stay in sync with the column marked as default sorted column in DeliveryProcessDefinitionSearch.vue.
export const DEFAULT_ORDER = 'label';

export const client = new DeliveryProcessDefinitionClient('', createInstance(), dateReviver);

export const useDeliveryProcessDefinitionListStore = defineStore(
    'configuration-delivery-process-definition-list-store',
    {
        state: (): DeliveryProcessDefinitionStoreState => ({
            filter: {
                order: DEFAULT_ORDER,
                orderDirection: OrderDirection.Ascending,
                pageNumber: 1,
                pageSize: 25,
                organisation: null
            },
            totalCount: 0,
            loading: false,
            items: []
        }),
        getters: {
            metaData(): {
                totalRows: number;
                resultsPerPage: number;
                currentPage: number | undefined;
                serverside: boolean;
            } {
                return {
                    totalRows: this.totalCount,
                    resultsPerPage: this.filter.pageSize,
                    currentPage: this.filter.pageNumber,
                    serverside: true
                };
            },
            getDefinition(): (id: number) => DeliveryProcessDefinitionSearchResultDto | undefined {
                return (id: number) => this.items?.find((item) => item.id === id);
            },
            search(): (query: string) => DeliveryProcessDefinitionSearchResultDto[] {
                return (query) => {
                    if (!query) {
                        return this.items;
                    } else {
                        return this.items?.filter((x) => x.label?.toLowerCase().includes(query.toLowerCase()));
                    }
                };
            }
        },
        actions: {
            init(filter: DeliveryProcessDefinitionFilter) {
                this.filter = { ...this.filter, ...filter };
                this.items = [];
            },
            clearFilter() {
                this.filter = {
                    order: DEFAULT_ORDER,
                    orderDirection: OrderDirection.Ascending,
                    pageNumber: 1,
                    pageSize: 25,
                    organisation: null
                };

                useAlertStore().clearAlert();

                this.items = [];
            },
            updateItem(item: DeliveryProcessDefinitionSearchResultDto) {
                const index = this.items?.findIndex((i) => i.id === item.id);
                if (index !== undefined && index !== -1) {
                    this.items?.splice(index, 1, item);
                }
            },
            resetPager() {
                this.filter.pageNumber = 1;
            },
            async getDefinitions() {
                this.loading = true;

                useAlertStore().clearAlert();

                const order = mapToQueryableOrder(this.filter.order);
                try {
                    const response = await client.search(
                        this.filter.id,
                        this.filter.label,
                        this.filter.description,
                        '',
                        this.filter.organisation?.ovoNumber,
                        this.filter.statusType,
                        order,
                        this.filter.orderDirection,
                        this.filter.pageNumber,
                        this.filter.pageSize
                    );

                    this.items = response.items;
                    this.filter.pageNumber = response.page;
                    this.filter.pageSize = response.pageSize;
                    this.totalCount = response.totalCount;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.list.error.title'),
                        error,
                        false
                    );
                } finally {
                    this.loading = false;
                }
            },
            updatePage(pageNumber: number) {
                this.filter.pageNumber = pageNumber;
            },
            updateSorting(column: string, order: string) {
                this.filter.order = column;
                this.filter.orderDirection = order === 'Asc' ? OrderDirection.Ascending : OrderDirection.Descending;
            },
            async getAll({ silent = false } = {}) {
                if (!silent) {
                    this.loading = true;
                }

                useAlertStore().clearAlert();
                try {
                    const response = await client.getAll();
                    this.items = response;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('deliveryprocessdefinition.list.error.title'),
                        error,
                        false
                    );
                } finally {
                    this.loading = false;
                }

                return this.items;
            }
        }
    }
);

function mapToQueryableOrder(order: string) {
    switch (order) {
        case 'statusId':
            return 'status';
        case 'ovoNumber':
            return 'organisation.ovoNumber';
        case 'organisation':
            return 'organisation.name';
        default:
            return order;
    }
}
